import { NativeModules, Platform } from 'react-native';
import type { KeyValueStoragePort } from '../core/application/ports/key-value-storage-port';

import {
  isSupportedLanguage,
  type SupportedLanguage,
} from '../shared/types/supported-language';
export type { SupportedLanguage } from '../shared/types/supported-language';

export const LANGUAGE_STORAGE_KEY = 'profile:language';

export const normalizeSupportedLanguage = (
  locale: unknown,
): SupportedLanguage | null => {
  if (typeof locale !== 'string') return null;

  const language = locale.trim().toLowerCase().split(/[-_]/)[0];
  return isSupportedLanguage(language) ? language : null;
};

export const persistServerLanguagePreference = async (
  storage: Pick<KeyValueStoragePort, 'setString'>,
  locale: unknown,
): Promise<SupportedLanguage | null> => {
  const language = normalizeSupportedLanguage(locale);
  if (!language) return null;

  await storage.setString(LANGUAGE_STORAGE_KEY, language);
  return language;
};

export const resolveSupportedLanguage = (
  candidates: readonly unknown[],
): SupportedLanguage => {
  for (const candidate of candidates) {
    const language = normalizeSupportedLanguage(candidate);
    if (language) return language;
  }

  return 'en';
};

export const resolveInitialLanguage = (
  bootstrapLocale: unknown,
  systemLocale: unknown,
): SupportedLanguage =>
  resolveSupportedLanguage([bootstrapLocale, systemLocale]);

/**
 * The authenticated server locale remains authoritative for the whole startup
 * session even when browser storage is unavailable or still contains an older
 * preference. Outside an authenticated bootstrap the persisted preference
 * continues to win over the system locale.
 */
export const resolveRuntimeLanguage = (
  bootstrapLocale: unknown,
  persistedLocale: unknown,
  systemLocale: unknown,
): SupportedLanguage =>
  resolveSupportedLanguage([bootstrapLocale, persistedLocale, systemLocale]);

const getBrowserLanguages = (): readonly string[] => {
  if (typeof navigator === 'undefined') return [];
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return navigator.languages;
  }

  return navigator.language ? [navigator.language] : [];
};

const getIntlLocale = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().locale || null;
  } catch {
    return null;
  }
};

export const detectSystemLanguage = (): SupportedLanguage => {
  const nativeLanguage =
    Platform.OS === 'ios'
      ? NativeModules.SettingsManager?.settings?.AppleLocale ||
        NativeModules.SettingsManager?.settings?.AppleLanguages?.[0]
      : Platform.OS === 'android'
      ? NativeModules.I18nManager?.localeIdentifier
      : null;

  return resolveSupportedLanguage([
    nativeLanguage,
    ...getBrowserLanguages(),
    getIntlLocale(),
  ]);
};
